import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import API from "../lib/api";
import { Warning, EnvelopeSimple, EnvelopeOpen, UserMinus, XCircle, CheckCircle, ArrowRight, CalendarBlank, BellRinging } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Phase 266 — "Tomorrow's Jobs at Risk" admin dashboard widget.
 *
 * Pulls GET /api/dashboard/tomorrow-jobs-at-risk and surfaces jobs scheduled
 * for tomorrow that look risky tonight — primarily jobs whose assigned
 * supplier hasn't opened the acknowledgment email yet, plus a few related
 * "couldn't email" / "unassigned" signals.
 *
 * Goal: catch problems the night before, not 60 minutes after the no-show.
 */

const RISK_META = {
  no_assignment: {
    Icon: UserMinus,
    tone: "red",
    short: "Unassigned",
  },
  send_failed: {
    Icon: XCircle,
    tone: "red",
    short: "Email failed",
  },
  no_recipient: {
    Icon: Warning,
    tone: "amber",
    short: "No recipient",
  },
  not_opened: {
    Icon: EnvelopeSimple,
    tone: "amber",
    short: "Not opened",
  },
  no_email_sent: {
    Icon: EnvelopeSimple,
    tone: "slate",
    short: "No ack email",
  },
};

const TONE_CLS = {
  red: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 border-red-200 dark:border-red-800/60",
  amber: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 border-amber-200 dark:border-amber-800/60",
  slate: "bg-slate-100 text-slate-700 dark:bg-slate-800/60 dark:text-slate-300 border-slate-200 dark:border-slate-700/60",
};

function formatTomorrow(dateStr) {
  if (!dateStr) return "tomorrow";
  try {
    return new Date(dateStr + "T00:00:00").toLocaleDateString("en-GB", {
      weekday: "long", day: "numeric", month: "short",
    });
  } catch {
    return "tomorrow";
  }
}

export default function TomorrowJobsAtRiskWidget() {
  const [data, setData] = useState({ items: [], total_scheduled_tomorrow: 0, total_at_risk: 0, date: "" });
  const [loading, setLoading] = useState(true);
  const [nudgingId, setNudgingId] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await API.get("/dashboard/tomorrow-jobs-at-risk");
      setData(data);
    } catch {
      // non-fatal, keep stale data
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 5 * 60_000); // every 5 minutes
    return () => clearInterval(t);
  }, [load]);

  const handleNudge = async (e, jobId) => {
    e.preventDefault();
    e.stopPropagation();
    setNudgingId(jobId);
    try {
      const { data: res } = await API.post(`/jobs/${jobId}/nudge-supplier`);
      const bits = [];
      if (res.email?.sent) bits.push(`email → ${res.email.to}`);
      if (res.sms?.sent) bits.push(`SMS → ${res.sms.to}`);
      if (bits.length === 0) {
        const reason = res.sms?.reason || res.email?.error || "No channels reached";
        toast.error(`Couldn't reach ${res.recipient_name || "supplier"}: ${reason}`);
      } else {
        toast.success(`Nudged ${res.recipient_name || res.recipient_kind}: ${bits.join(" + ")}`);
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Nudge failed");
    } finally {
      setNudgingId("");
    }
  };

  const tomorrowLabel = formatTomorrow(data.date);
  const items = data.items || [];
  const allClear = !loading && data.total_scheduled_tomorrow > 0 && items.length === 0;

  return (
    <div
      className="border border-border rounded-md bg-card animate-fade-in"
      data-testid="widget-tomorrow-jobs-at-risk"
    >
      <div className="px-4 py-3 border-b border-border flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <CalendarBlank size={16} className="text-primary" />
          <h3 className="font-semibold text-sm font-[Chivo]">
            Tomorrow's Jobs at Risk
            <span className="ml-2 text-xs font-normal text-muted-foreground">({tomorrowLabel})</span>
          </h3>
        </div>
        <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
          <span data-testid="tomorrow-risk-total-scheduled">
            <strong className="text-foreground">{data.total_scheduled_tomorrow}</strong> scheduled
          </span>
          <span className="opacity-50">·</span>
          <span data-testid="tomorrow-risk-total-at-risk">
            <strong className={items.length > 0 ? "text-amber-600 dark:text-amber-400" : "text-foreground"}>
              {items.length}
            </strong> at risk
          </span>
        </div>
      </div>

      {loading && items.length === 0 ? (
        <p className="px-4 py-6 text-xs text-muted-foreground text-center" data-testid="tomorrow-risk-loading">Loading…</p>
      ) : data.total_scheduled_tomorrow === 0 ? (
        <p className="px-4 py-8 text-xs text-muted-foreground text-center" data-testid="tomorrow-risk-empty-noschedule">
          No jobs scheduled for {tomorrowLabel}.
        </p>
      ) : allClear ? (
        <div className="px-4 py-6 flex items-center justify-center gap-2 text-emerald-600 dark:text-emerald-400" data-testid="tomorrow-risk-all-clear">
          <CheckCircle size={16} weight="fill" />
          <p className="text-xs font-medium">All {data.total_scheduled_tomorrow} jobs look good — acknowledgment emails opened.</p>
        </div>
      ) : (
        <ul className="divide-y divide-border" data-testid="tomorrow-risk-list">
          {items.slice(0, 8).map(it => {
            const meta = RISK_META[it.risk_code] || RISK_META.not_opened;
            const Icon = meta.Icon;
            const canNudge = it.risk_code !== "no_assignment" && !!it.assigned_name;
            const isNudging = nudgingId === it.id;
            return (
              <li key={it.id} data-testid={`tomorrow-risk-item-${it.id}`} className="row-hover transition-colors">
                <div className="flex items-center gap-3 px-4 py-2.5">
                  <Link
                    to={`/jobs/${it.id}`}
                    className="flex items-center gap-3 flex-1 min-w-0"
                    data-testid={`tomorrow-risk-link-${it.id}`}
                  >
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md border text-[10px] font-semibold shrink-0 ${TONE_CLS[meta.tone]}`} data-testid={`tomorrow-risk-pill-${it.risk_code}-${it.id}`}>
                      <Icon size={11} weight="bold" />
                      {meta.short}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-xs font-medium truncate">{it.title}</p>
                        <span className="text-[10px] font-mono text-muted-foreground">{it.reference}</span>
                        {it.start_time && (
                          <span className="text-[10px] text-muted-foreground">· {it.start_time}</span>
                        )}
                      </div>
                      <p className="text-[10px] text-muted-foreground truncate mt-0.5">
                        {it.assigned_name || "Unassigned"}
                        {it.address ? <> · {it.address}</> : null}
                      </p>
                      <p className="text-[10px] text-muted-foreground/80 mt-0.5">
                        {it.risk_label}
                        {it.risk_code === "not_opened" && it.ack_email_sent_to ? <> · sent to {it.ack_email_sent_to}</> : null}
                      </p>
                    </div>
                  </Link>
                  {canNudge ? (
                    <button
                      onClick={(e) => handleNudge(e, it.id)}
                      disabled={isNudging}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-primary/30 bg-primary/5 hover:bg-primary/10 text-primary text-[10px] font-semibold shrink-0 transition-colors disabled:opacity-50"
                      title={`Send a reminder email + SMS to ${it.assigned_name}`}
                      data-testid={`tomorrow-risk-nudge-${it.id}`}
                    >
                      <BellRinging size={11} weight="fill" />
                      {isNudging ? "Nudging…" : "Nudge"}
                    </button>
                  ) : null}
                  <Link to={`/jobs/${it.id}`} className="shrink-0" aria-label="Open job">
                    <ArrowRight size={12} className="text-muted-foreground" />
                  </Link>
                </div>
              </li>
            );
          })}
          {items.length > 8 && (
            <li className="px-4 py-2 text-[10px] text-muted-foreground text-center" data-testid="tomorrow-risk-overflow">
              +{items.length - 8} more — open <Link to="/jobs" className="text-primary hover:underline">Jobs</Link> to review
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
